import React, { useEffect, useState } from 'react'
import api from 'helpers/api'
import { useAuth } from 'hooks/auth'
import { useApplicationStore } from 'hooks/application'
import { NotificationsContext, NotificationsStore } from 'hooks/notifications'
import { IUserNotification } from 'types'

const useNotificationsProviderValue = (): NotificationsStore => {
  'use no memo'
  const { user } = useAuth()
  const { displayErrorMessage } = useApplicationStore()

  const [unreadCount, setUnreadCount] = useState<number>(0)
  const [notifications, setNotifications] = useState<IUserNotification[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [nextPageToken, setNextPageToken] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState<boolean>(false)

  const refreshUnreadCount = async (): Promise<void> => {
    if (!user) return
    try {
      // size=1: this runs on every badge refresh and only needs the metadata.
      const response = await api.getUserNotifications(1, false, 1)
      setUnreadCount(response.metadata.unreadCount)
    } catch {
      // Silent: a failed badge refresh must never surface an error toast.
    }
  }

  const loadNotifications = async (reset?: boolean): Promise<void> => {
    if (!user) return
    setLoading(true)
    try {
      const response = await api.getUserNotifications(1, false, 25, reset ? undefined : (nextPageToken ?? undefined))
      setNotifications((previous) => (reset ? response.data : [...previous, ...response.data]))
      setUnreadCount(response.metadata.unreadCount)
      setHasMore(response.metadata.nextPageToken !== null)
      setNextPageToken(response.metadata.nextPageToken)
    } catch (e) {
      displayErrorMessage(e)
    } finally {
      setLoading(false)
    }
  }

  const markRead = async (notification: IUserNotification): Promise<void> => {
    if (notification.readAt) return
    const readAt = new Date().toISOString()
    setNotifications((previous) =>
      previous.map((item) => (item.publicId === notification.publicId ? { ...item, readAt } : item))
    )
    setUnreadCount((count) => Math.max(0, count - 1))
    try {
      await api.updateUserNotification(notification.publicId, { read: true })
    } catch {
      // Re-sync the feed, badge and pagination after a rejected optimistic update.
      await loadNotifications(true)
    }
  }

  const dismiss = async (notification: IUserNotification): Promise<void> => {
    setNotifications((previous) => previous.filter((item) => item.publicId !== notification.publicId))
    if (!notification.readAt) {
      setUnreadCount((count) => Math.max(0, count - 1))
    }
    try {
      await api.updateUserNotification(notification.publicId, { dismissed: true })
    } catch {
      await loadNotifications(true)
    }
  }

  const markAllRead = async (): Promise<void> => {
    const readAt = new Date().toISOString()
    setNotifications((previous) => previous.map((item) => (item.readAt ? item : { ...item, readAt })))
    setUnreadCount(0)
    try {
      await api.markAllNotificationsRead()
    } catch {
      await loadNotifications(true)
    }
  }

  useEffect(() => {
    if (!user?.publicId) return

    // Notification timing is private too. Refresh through authenticated REST
    // until the websocket service supports authenticated per-user rooms.
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refreshUnreadCount()
    }
    const interval = window.setInterval(refreshWhenVisible, 30_000)
    window.addEventListener('focus', refreshWhenVisible)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    void refreshUnreadCount()

    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', refreshWhenVisible)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [user?.publicId])

  return {
    unreadCount,
    notifications,
    loading,
    hasMore,
    refreshUnreadCount,
    loadNotifications,
    markRead,
    dismiss,
    markAllRead
  }
}

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  'use no memo'
  const value = useNotificationsProviderValue()
  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
}
